import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { hasValue } from 'src/shared/utils/common';
import { getRandomNumber } from 'src/shared/utils/get-random-number';

import { TGeoLoadErrors, TGeologicalLoadData } from '../../geological-load-tab/types';

export class GeologicalLoad {
  readonly entityId: number;

  readonly initialData: TGeologicalLoadData | null;
  @observable private readonly _data: TGeologicalLoadData;
  @observable private _errors: TGeoLoadErrors = {};

  constructor(initialData?: TGeologicalLoadData | null) {
    this.entityId = getRandomNumber(10);
    this.initialData = initialData ?? null;
    this._data = initialData ?? {};

    makeObservable(this);
  }

  get data(): TGeologicalLoadData {
    return toJS(this._data);
  }

  @computed
  get daysSumm(): number | null {
    if (
      !hasValue(this.coreSamplingDayAmount) &&
      !hasValue(this.daysAmountOfDataInterpretationOperation) &&
      !hasValue(this.testingFormationWorkDayAmount)
    ) {
      return null;
    }

    return (
      (this.coreSamplingDayAmount ?? 0) +
      (this.daysAmountOfDataInterpretationOperation ?? 0) +
      (this.testingFormationWorkDayAmount ?? 0)
    );
  }

  @computed
  get errors(): TGeoLoadErrors {
    return toJS(this._errors);
  }

  @action.bound
  clearErrors(): void {
    this._errors = {};
  }

  @action.bound
  addError(fieldId: keyof TGeologicalLoadData, errorText: string): void {
    this._errors = { ...this._errors, [fieldId]: errorText };
  }

  @action.bound
  removeError(fieldId: keyof TGeologicalLoadData): void {
    const errors: TGeoLoadErrors = {};

    for (const key in this._errors) {
      if (key === fieldId) {
        continue;
      }

      errors[key as keyof TGeologicalLoadData] = this._errors[key as keyof TGeologicalLoadData];
    }

    this._errors = errors;
  }

  @action.bound
  clearData(): void {
    for (const key in this._data) {
      this._data[key as keyof TGeologicalLoadData] = undefined;
    }
  }

  @computed
  get coreSelection(): boolean {
    return !!this._data.coreSelection;
  }

  @action.bound
  setCoreSelection(isEnabled: boolean): void {
    this._data['coreSelection'] = isEnabled;
  }

  @computed
  get testingFormationWork(): boolean {
    return !!this._data.testingFormationWork;
  }

  @action.bound
  setTestingFormationWork(isEnabled: boolean): void {
    this._data['testingFormationWork'] = isEnabled;
  }

  @computed
  get dataInterpretation(): boolean {
    return !!this._data.dataInterpretation;
  }

  @action.bound
  setDataInterpretation(isEnabled: boolean): void {
    this._data['dataInterpretation'] = isEnabled;
  }

  @computed
  get coreSamplingIntervalId(): number[] {
    return this._data.coreSamplingIntervalId ?? [];
  }

  @action.bound
  setCoreSamplingIntervalId(value: number[]): void {
    this._data['coreSamplingIntervalId'] = value;
  }

  @computed
  get testingFormationWorkAmount(): number | null {
    return this._data.testingFormationWorkAmount ?? null;
  }

  @action.bound
  setTestingFormationWorkAmount(value: number | null): void {
    this._data['testingFormationWorkAmount'] = value ?? undefined;
  }

  @computed
  get numberOfDataInterpretationOperations(): number | null {
    return this._data.numberOfDataInterpretationOperations ?? null;
  }

  @action.bound
  setNumberOfDataInterpretationOperations(value: number | null): void {
    this._data['numberOfDataInterpretationOperations'] = value ?? undefined;
  }

  @computed
  get extractionVolume(): number | null {
    return this._data.extractionVolume ?? null;
  }

  @action.bound
  setExtractionVolume(value: number | null): void {
    this._data['extractionVolume'] = value ?? undefined;
  }

  @computed
  get coreSamplingDayAmount(): number | null {
    return this._data.coreSamplingDayAmount ?? null;
  }

  @action.bound
  setCoreSamplingDayAmount(value: number | null): void {
    this._data['coreSamplingDayAmount'] = value ?? undefined;
  }

  @computed
  get testingFormationWorkDayAmount(): number | null {
    return this._data.testingFormationWorkDayAmount ?? null;
  }

  @action.bound
  setTestingFormationWorkDayAmount(value: number | null): void {
    this._data['testingFormationWorkDayAmount'] = value ?? undefined;
  }

  @computed
  get daysAmountOfDataInterpretationOperation(): number | null {
    return this._data.daysAmountOfDataInterpretationOperation ?? null;
  }

  @action.bound
  setDaysAmountOfDataInterpretationOperation(value: number | null): void {
    this._data['daysAmountOfDataInterpretationOperation'] = value ?? undefined;
  }
}
